import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  TableChart,
  HorizontalRule,
  Title,
  PostAdd as TemplateIcon,
} from "@mui/icons-material";

const TEMPLATES = [
  {
    name: "Minutes of Meeting (MOM)",
    html: `<h2>Minutes of Meeting (MOM)</h2>
<p><strong>Date & Time:</strong> [Enter Date / Time]</p>
<p><strong>Discussion Leader:</strong> [Name]</p>
<hr/>
<h3>1. Meeting Objectives</h3>
<p>Summary of core purpose and objectives discussed.</p>
<h3>2. Discussion Points</h3>
<ul>
  <li>Topic 1: Detailed notes, findings, and inputs from participants.</li>
  <li>Topic 2: Analysis of project roadmap and timeline.</li>
</ul>
<h3>3. Key Decisions</h3>
<ul>
  <li>Decision 1: Approved approach for next milestone.</li>
</ul>
<h3>4. Action Items & Next Steps</h3>
<table border="1" cellpadding="8" style="width:100%; border-collapse: collapse;">
  <thead>
    <tr>
      <th>Action Item</th>
      <th>Assignee</th>
      <th>Target Date</th>
      <th>Status</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Finalize architecture review</td>
      <td>Lead Developer</td>
      <td>End of Week</td>
      <td>Pending</td>
    </tr>
  </tbody>
</table>`,
  },
  {
    name: "Meeting Agenda",
    html: `<h2>Meeting Agenda</h2>
<p><strong>Objective:</strong> Align on deliverables and remove blockers.</p>
<hr/>
<h3>Schedule of Topics:</h3>
<ol>
  <li><strong>Opening & Roll Call</strong> (5 mins)</li>
  <li><strong>Review of Previous Action Items</strong> (10 mins)</li>
  <li><strong>Core Presentation & Demo</strong> (25 mins)</li>
  <li><strong>Q&A and Open Discussion</strong> (15 mins)</li>
  <li><strong>Next Steps & Summary</strong> (5 mins)</li>
</ol>`,
  },
  {
    name: "1-on-1 Sync Notes",
    html: `<h2>1-on-1 Sync Notes</h2>
<hr/>
<h3>Updates & Highlights:</h3>
<ul>
  <li>Wins and progress since last check-in.</li>
</ul>
<h3>Challenges & Blockers:</h3>
<ul>
  <li>Areas needing assistance or escalation.</li>
</ul>
<h3>Follow-up Commitments:</h3>
<ul>
  <li>Follow-up item 1</li>
</ul>`,
  },
];

export default function HtmlEditor({
  value = "",
  onChange,
  placeholder = "Type meeting agenda, discussion points, minutes, or action items here...",
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const editorRef = useRef(null);
  const lastValueRef = useRef(value || "");
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);

  // Initialize on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || "";
      lastValueRef.current = value || "";
    }
  }, []);

  // Synchronize ONLY when value changes from an external source (not internal typing)
  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value || "";
      if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastValueRef.current = html;
      if (onChange) {
        onChange(html);
      }
    }
  };

  const executeCommand = (command, val = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, val);
      handleInput();
    }
  };

  const handleFormatBlock = (tag) => {
    executeCommand("formatBlock", `<${tag}>`);
  };

  const handleInsertTable = () => {
    const tableHtml = `
      <table border="1" cellpadding="8" style="width:100%; border-collapse: collapse; margin: 12px 0;">
        <thead>
          <tr>
            <th>Item</th>
            <th>Owner</th>
            <th>Target Date</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Action point 1</td>
            <td>Team Member</td>
            <td>TBD</td>
            <td>Open</td>
          </tr>
        </tbody>
      </table>
    `;
    executeCommand("insertHTML", tableHtml);
  };

  const applyTemplate = (template) => {
    const content = template.html;
    lastValueRef.current = content;
    if (editorRef.current) {
      editorRef.current.innerHTML = content;
      editorRef.current.focus();
    }
    if (onChange) {
      onChange(content);
    }
    setTemplateMenuAnchor(null);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: isDark ? "rgba(255, 255, 255, 0.12)" : "#cbd5e1",
        borderRadius: "14px",
        bgcolor: isDark ? "#111c44" : "#ffffff",
        overflow: "hidden",
        boxShadow: isDark ? "none" : "0 2px 6px rgba(112, 144, 176, 0.08)",
        transition: "border-color 0.2s ease",
        "&:focus-within": {
          borderColor: "#f59e0b",
        },
      }}
    >
      {/* TOOLBAR */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.5,
          p: 1.25,
          borderBottom: "1px solid",
          borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#e2e8f0",
          bgcolor: isDark ? "#0b1437" : "#f8fafc",
        }}
      >
        {/* Headings */}
        <Tooltip title="Heading 1">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormatBlock("h1")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <Title fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Heading 2">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormatBlock("h2")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: "0.75rem" }}>H2</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Heading 3">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormatBlock("h3")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: "0.72rem" }}>H3</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Normal Paragraph">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormatBlock("p")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.7rem" }}>P</Typography>
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {/* Inline styles */}
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("bold")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("italic")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("underline")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("strikeThrough")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <StrikethroughS fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {/* Lists & Structural */}
        <Tooltip title="Bullet List">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("insertUnorderedList")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered List">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("insertOrderedList")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Table">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleInsertTable}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <TableChart fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Divider Line">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("insertHorizontalRule")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <HorizontalRule fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Blockquote">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => executeCommand("formatBlock", "<blockquote>")}
            sx={{ color: isDark ? "#a3aed0" : "#64748b" }}
          >
            <FormatQuote fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        {/* Pre-made Templates dropdown */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<TemplateIcon fontSize="small" sx={{ color: "#8b5cf6" }} />}
          onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
          sx={{
            textTransform: "none",
            height: 30,
            fontSize: "0.75rem",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: isDark ? "rgba(255, 255, 255, 0.12)" : "#cbd5e1",
            color: isDark ? "#ffffff" : "#1b2559",
            "&:hover": {
              borderColor: "#8b5cf6",
              bgcolor: isDark ? "rgba(139, 92, 246, 0.1)" : "#faf5ff",
            },
          }}
        >
          Templates
        </Button>
        <Menu
          anchorEl={templateMenuAnchor}
          open={Boolean(templateMenuAnchor)}
          onClose={() => setTemplateMenuAnchor(null)}
          PaperProps={{
            sx: { borderRadius: "12px", bgcolor: isDark ? "#111c44" : "#ffffff" },
          }}
        >
          {TEMPLATES.map((tmpl) => (
            <MenuItem
              key={tmpl.name}
              onClick={() => applyTemplate(tmpl)}
              sx={{ fontSize: "0.85rem", fontWeight: 600 }}
            >
              {tmpl.name}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* RICH VISUAL EDITOR AREA */}
      <Box sx={{ p: 2.5, minHeight: 220, maxHeight: 420, overflowY: "auto" }}>
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          sx={{
            outline: "none",
            minHeight: 180,
            color: isDark ? "#cbd5e1" : "#334155",
            fontSize: "0.95rem",
            lineHeight: 1.7,
            "&:empty:before": {
              content: `attr(data-placeholder)`,
              color: isDark ? "#718096" : "#94a3b8",
              fontStyle: "italic",
              pointerEvents: "none",
              display: "block",
            },
            "& h1": { fontSize: "1.5rem", fontWeight: 800, mb: 1.5, color: isDark ? "#ffffff" : "#1b2559" },
            "& h2": { fontSize: "1.25rem", fontWeight: 800, mt: 2, mb: 1, color: isDark ? "#ffffff" : "#1b2559" },
            "& h3": { fontSize: "1.05rem", fontWeight: 700, mt: 1.5, mb: 0.75, color: isDark ? "#ffffff" : "#1b2559" },
            "& p": { lineHeight: 1.6, mb: 1.5 },
            "& ul, & ol": { pl: 3, mb: 1.5 },
            "& li": { mb: 0.5 },
            "& table": {
              width: "100%",
              borderCollapse: "collapse",
              my: 2,
              borderRadius: "8px",
              overflow: "hidden",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
            },
            "& th": {
              bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
              p: 1.25,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#cbd5e1"}`,
              textAlign: "left",
              fontWeight: 700,
              color: isDark ? "#ffffff" : "#1b2559",
            },
            "& td": {
              p: 1.25,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
            },
            "& blockquote": {
              borderLeft: "4px solid #f59e0b",
              pl: 2,
              py: 0.5,
              fontStyle: "italic",
              my: 1.5,
              color: isDark ? "#a3aed0" : "#64748b",
              bgcolor: isDark ? "rgba(245, 158, 11, 0.05)" : "#fffbeb",
              borderRadius: "0 8px 8px 0",
            },
            "& hr": {
              border: 0,
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
              my: 2,
            },
          }}
          data-placeholder={placeholder}
        />
      </Box>
    </Box>
  );
}
